import { useEffect, useState, type ReactNode } from 'react';
import { useConnection, useReadContract } from 'wagmi';
import { referralManagerAbi } from '@syntra/abi';
import { DEPLOYMENT as D } from '../../config/env.ts';
import { forgetInvite, storedInvite, useInviteSponsor } from '../../hooks/useInvite.ts';
import { useI18n } from '../../i18n/index.tsx';
import { shortAddr } from '../../lib/format.ts';
import { Notice } from '../ui/primitives.tsx';
import { ActionButton } from './ActionButton.tsx';

const ZERO = '0x0000000000000000000000000000000000000000';

/**
 * Guards an action that stakes (a stake, the SYN → sSYN conversion, claiming a bond into staking). The contract binds a
 * wallet that has not joined a referral team to the project's default sponsor at its first stake, and binding is
 * permanent. So when this wallet has not joined and an invite link brought it here, it joins that referrer first and
 * only then sees the action; without an invite, a note says where the stake will place it.
 */
export function JoinFirst({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { address } = useConnection();
  const bound = useReadContract({
    address: D.referral,
    abi: referralManagerAbi,
    functionName: 'isBound',
    args: [address ?? ZERO],
    query: { enabled: Boolean(address), refetchInterval: 15_000 },
  });
  const [invite] = useState(storedInvite);
  const lookup = useInviteSponsor(invite?.code ?? null);
  useEffect(() => {
    if (bound.data === true) forgetInvite(); // joined: the invite has done its job
  }, [bound.data]);

  if (!address || bound.data !== false) return <>{children}</>;
  const sponsor = lookup.data;
  if (invite && sponsor && sponsor.toLowerCase() !== address.toLowerCase()) {
    return (
      <div className="join-first" data-testid="join-first">
        <Notice tone="warn">
          {t('join.invited', { sponsor: shortAddr(sponsor), code: invite.code, side: t(invite.side === 'R' ? 'join.right' : 'join.left') })}
        </Notice>
        <ActionButton
          label={t('join.cta')}
          request={{ address: D.referral, abi: referralManagerAbi, functionName: 'bind', args: [sponsor, invite.side === 'R' ? 1 : 0] }}
          onDone={() => void bound.refetch()}
        />
      </div>
    );
  }
  return (
    <>
      <p className="muted join-note" data-testid="join-note">
        {t('join.none')}
      </p>
      {children}
    </>
  );
}
